import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EntityCard } from '@/components/EntityCard';
import { charactersMessages } from './charactersPage.messages';
import { fetchCharacters } from '@/api/services/characters/fetchCharacters';
import { softDeleteCharacter } from '@/api/services/characters/softDeleteCharacter';
import type { CharacterWithUrls } from 'schemas';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CardsGridContainer } from '@/components/layout/CardsGridContainer';
import { Paragraph, Muted } from '@/components/Typography';
import { ROUTES } from '@/routes';
import { transformCharacterToEntityCard } from '@/pages/CharactersPage/helpers/transformCharacterToEntityCard';
import { DashboardPageContainer } from '@/components/layout/DashboardPageContainer';
import { sharedMessages } from '@/shared-messages/shared.messages';
import { useLoadOnce } from '@/hooks/useLoadOnce';

import { toast } from 'sonner';

export function CharactersPage() {
  const navigate = useNavigate();
  const [characters, setCharacters] = useState<CharacterWithUrls[]>([]);
  const [loading, setLoading] = useState(true);

  const [deletingCharacterId, setDeletingCharacterId] = useState<string | null>(
    null
  );
  const [characterToDelete, setCharacterToDelete] =
    useState<CharacterWithUrls | null>(null);

  const goToCharacterForm = () => {
    navigate(ROUTES.DASHBOARD_CHARACTERS_CREATE);
  };

  const handleDeleteCharacter = (character: CharacterWithUrls) => {
    setCharacterToDelete(character);
  };

  const confirmDeleteCharacter = async () => {
    if (!characterToDelete) return;

    try {
      setDeletingCharacterId(characterToDelete.id);
      await softDeleteCharacter(characterToDelete.id);

      // Remove the deleted character from the local state
      setCharacters((prevCharacters) =>
        prevCharacters.filter(
          (character) => character.id !== characterToDelete.id
        )
      );

      toast.success(charactersMessages.deleteSuccessMessage);
    } catch {
      toast.error(charactersMessages.deleteErrorMessage);
    } finally {
      setDeletingCharacterId(null);
      setCharacterToDelete(null);
    }
  };

  const cancelDeleteCharacter = () => {
    setCharacterToDelete(null);
  };

  useLoadOnce(() => {
    const loadCharacters = async () => {
      try {
        setLoading(true);

        const fetchedCharacters = await fetchCharacters();
        setCharacters(fetchedCharacters);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    loadCharacters();
  });

  return (
    <DashboardPageContainer
      headline={charactersMessages.title}
      cta={
        <Button onClick={goToCharacterForm}>
          {charactersMessages.createButton}
        </Button>
      }
    >
      {/* Character Grid */}
      {characters.length === 0 && !loading ? (
        <div className="flex h-64 flex-col items-center justify-center text-center">
          <Paragraph>{charactersMessages.noCharactersFound}</Paragraph>
          <Muted>{charactersMessages.noCharactersDescription}</Muted>
          <Button onClick={goToCharacterForm} variant="default">
            {charactersMessages.createButton}
          </Button>
        </div>
      ) : (
        <CardsGridContainer loading={loading}>
          {characters.map((character) => {
            const entityCardProps = transformCharacterToEntityCard(character);
            const isDeleting = deletingCharacterId === character.id;
            return (
              <EntityCard
                key={character.id}
                {...entityCardProps}
                actions={[
                  {
                    actionButtonLabel: isDeleting
                      ? charactersMessages.deleting
                      : charactersMessages.deleteAction,
                    onButtonClick: () => handleDeleteCharacter(character),
                  },
                ]}
              />
            );
          })}
        </CardsGridContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={characterToDelete !== null}
        onOpenChange={cancelDeleteCharacter}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {charactersMessages.deleteConfirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {charactersMessages.deleteConfirmMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteCharacter}>
              {sharedMessages.commonButtons.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCharacter}
              disabled={deletingCharacterId !== null}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deletingCharacterId === characterToDelete?.id
                ? charactersMessages.deleting
                : charactersMessages.deleteAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardPageContainer>
  );
}
